/**
 * @file select.component.tsx
 * @description Компонент обертка для работы с @consta/uikit/Select. Поддержка загрузки списка из redux.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-11
 * @dependencies []
 * @related-files []
 */

import { useEffect, useMemo } from 'react';
import { Select as ConstaSelect } from '@consta/uikit/Select';
import { useSelector, useDispatch } from 'react-redux';
import { getItems } from '@features/enums/enum-actions';
import { enumObject, getEnums } from '@features/enums/enum-slice';
import { AppDispatch } from '@store/index';
import { IBaseObjectDTO } from '@src/types/dto';

/**
 * Интерфейс для элемента выпадающего списка, наследуемый от TBaseObjectDto
 */
export interface ISelectItem extends IBaseObjectDTO {
    id: string;
    /** Группа к которой принадлежит элемент (опционально) */
    group?: string;
    /** Отключен ли элемент для выбора */
    disabled?: boolean;
}

/**
 * Props для компонента Select с поддержкой загрузки из Redux
 */
export interface ISelectProps {
    /** Тип enum для автоматической загрузки из Redux store */
    type?: string;
    /** Текст для пустого элемента в начале списка */
    emptyItem?: string;
    /** Текущее выбранное значение */
    value?: IBaseObjectDTO | null;
    /** Массив элементов для выбора (если не используется автозагрузка) */
    items?: ISelectItem[];
    /** Группы элементов для группированного отображения */
    groups?: IBaseObjectDTO[];
    /** Callback функция вызываемая при изменении выбора */
    onChange: (newValue: ISelectItem | null) => void;
}

//TODO: refactor получение списка из redux, сейчас компонент получает все и фильтрует, это много rerender
export const Select = ({ type = '', emptyItem = '', value = null, groups, onChange, ...props }: ISelectProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const enumItems: enumObject[] = useSelector(getEnums);
    const items: ISelectItem[] = useMemo(() => {
        const initialItems = emptyItem ? [{ name: emptyItem, id: '', $type: type }] : [];

        if (props.items) {
            return [...initialItems, ...props.items];
        }

        const currentEnum = enumItems.find((enumItem: enumObject) => enumItem.type === value?.$type);

        if (currentEnum) {
            return [...initialItems, ...currentEnum.items];
        }

        return initialItems;
    }, [enumItems, props.items]);

    useEffect(() => {
        if (value?.$type && !props.items && !enumItems?.find((enumItem: enumObject) => enumItem.type === value.$type)) {
            dispatch(getItems({ type: value.$type }));
        }
    }, []);

    return (
        <ConstaSelect
            size="s"
            {...props}
            onChange={(newValue: ISelectItem | null) => onChange(newValue)}
            items={items}
            getItemLabel={(item) => item.name}
            getItemKey={(item) => item.id}
            value={value}
        />
    );
};
